<template>
  <div class="gauge-widget" v-tooltip="objDesc">
    <svg viewBox="0 0 100 100" class="gauge">
      <!-- Tick marks arc (from 135° to 45°) -->
      <g>
        <line
          v-for="(angle, i) in tickAngles"
          :key="i"
          :x1="50 + innerRadius * Math.cos(angle * deg)"
          :y1="50 + innerRadius * Math.sin(angle * deg)"
          :x2="50 + outerRadius * Math.cos(angle * deg)"
          :y2="50 + outerRadius * Math.sin(angle * deg)"
          :stroke="i < activeTicks ? '#ffffffcc' : '#ffffff33'"
          stroke-width="2"
          stroke-linecap="round"
        />
      </g>

      <!-- Indicator pointer -->
      <line
        class="gauge-pointer"
        :x1="pointerAngle2.x1"
        :y1="pointerAngle2.y1"
        :x2="pointerAngle2.x2"
        :y2="pointerAngle2.y2"
        stroke="white"
        stroke-width="3"
        stroke-linecap="round"
      />

      <!-- Center pressure value -->
      <text x="50%" y="48%" text-anchor="middle" font-size="14" font-weight="bold" fill="#fcfaf2">
        {{ this.measure }}
      </text>
      <text x="50%" y="59%" text-anchor="middle" font-size="9" fill="#fcfaf2">
        {{ this.unit  }}
      </text>

      <!-- Down arrow -->
      <text x="30%" y="93%" text-anchor="middle" font-size="9" fill="white">↓{{ minValue }}</text>

      <!-- Up arrow -->
      <text x="70%" y="93%" text-anchor="middle" font-size="9" fill="white">{{ maxValue }}↑</text>
    </svg>
  </div>
</template>


<script>
import { defineComponent } from "vue";
// import dialogChartSingle from '@/components/dialog/chartSingle.vue';

export default defineComponent({
  name: "widgetGauge",
  setup() {},
  components: {
    // dialogChartSingle,
  },
  props: {
    label: {
      type: String,
      required: false
    },
    value: {
      type: Object,
      required: true
    },
    icon: {
      type: String,
      required: false
    },
    colorActive: {
      type: String,
      required: false
    },
    colorInactive: {
      type: String,
      required: false
    },
    colorText: {
      type: String,
      required: false
    },
    size: {
      type: String,
      required: true
    },
    min: {
      type: Number,
      required: false
    },
    max: {
      type: Number,
      required: false
    },
    bidirectional: {
      type: Boolean,
      required: false
    },
  },
  data() {
    return {
      loading: false,
      showChart1: false,
      startAngle: 135,
      endAngle: 405,
      zeroAngle: 270,
      innerRadius: 38,
      outerRadius: 44,
      minTicks: 25,
      maxTicks: 50,
    }
  },
  watch: {

  },
  computed: {
    iconLabel: function () {
      if (this.label !== undefined) return this.label;
      if (this.value.label !== undefined) return this.value.label;
      return '';
    },
    iconSymbol: function () {
      if (this.icon !== undefined) return this.icon;
      else return this.value.icon;
    },
    colorSymbol() {
      if (this.value.value) {
        if (this.colorActive !== undefined) return this.colorActive;
        else return `fg-lightGreen`;
      } else {
        if (this.colorInactive !== undefined) return this.colorInactive;
        else return `fg-darkGray`;
      }
    },
    colorTextLabel() {
      if (this.colorText !== undefined) return this.colorText;
      else return `fg-gray`;
    },
    isRecord() {
      if (typeof(this.value) === "object") {
        if (this.value.record === "true") return true;
      }
      return false;
    },
    measure() {
      return this.value.value;
    },
    unit() {
      return this.value.unit;
    },
    objLabel() {
      if (this.value.label) return this.value.label;
      else return "";
    },
    objDesc() {
      if (this.value.description) return this.value.description;
      else return "";
    },
    showPointer() {
      if (this.isRecord) return "cursor-pointer";
      else return "";
    },
    arrowStyle() {
      return ({
        transform: `rotate(${this.pointerAngle}deg)`,
        transformOrigin: '50% 50%',
        transition: 'transform 0.5s ease-in-out'
      });
    },
    directions() {
      return ['N', 'O', 'S', 'W'];
    },
    ticks() {
      const totalTicks = 36;
      const skipIndexes = [0, 9, 18, 27];
      return Array.from({ length: totalTicks }, (_, i) => i).filter(i => !skipIndexes.includes(i))
    },
    clampedValue() {
      return Math.max(-this.maxValue, Math.min(this.maxValue, this.value.value));
    },
    percent() {
      if (this.bidirectional) return (this.clampedValue + this.maxValue) / (2 * this.maxValue);
      else return Math.min(1, Math.max(0, (this.value.value - this.minValue) / (this.maxValue - this.minValue)));
    },
    activeTicks() {
      return Math.floor(this.percent * (this.totalTicks + 1));
    },
    tickAngles() {
      return Array.from({ length: this.totalTicks + 1 }, (_, i) => this.startAngle + i * this.tickStep);
    },
    pointerAngle() {
      // return this.startAngle + this.percent * (this.endAngle - this.startAngle);
      if (this.bidirectional) return this.startAngle + this.percent * (this.endAngle - this.startAngle);
      else return this.tickAngles[this.activeTicks] ?? this.tickAngles.at(-1)
    },
    pointerAngle2() {
      const angle = this.pointerAngle * (Math.PI / 180);
      return {
        x1: 50 + this.innerRadius * Math.cos(angle),
        y1: 50 + this.innerRadius * Math.sin(angle),
        x2: 50 + this.outerRadius * Math.cos(angle),
        y2: 50 + this.outerRadius * Math.sin(angle)
      }
    },
    tickStep() {
      return (this.endAngle - this.startAngle) / this.totalTicks;
    },
    deg() {
      return Math.PI / 180;
    },
    totalTicks() {
      const perc = Math.abs((this.maxValue-this.minValue)*0.1);
      if (perc > this.maxTicks) return this.maxTicks;
      else if (perc < this.minTicks) return this.minTicks;
      else return perc;
    },
    minValue() {
      if (this.measure < this.min) return this.measure;
      else return this.min;
    },
    maxValue() {
      if (this.measure > this.max) return this.measure;
      else return this.max;
    },
  },
  mounted() {
  },
  methods: {
    showChart(value) {
      if (this.isRecord) {
        if (value === 'value1') this.showChart1 = true;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.gauge-widget {
  background: radial-gradient(circle at center, #0089a7, #0b346e);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.gauge {
  width: 100%;
  height: 100%;
}
.gauge-pointer {
  stroke: white;
  stroke-width: 3;
  stroke-linecap: round;
  transition: x1 0.3s ease, y1 0.3s ease, x2 0.3s ease, y2 0.3s ease;
  filter: drop-shadow(0 0 3px rgba(255, 255, 255, 0.8));
}
</style>